import React from "react";
import { Layers, Compass, Handshake, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    id: "digital-product",
    title: "Digital Product Creation",
    description:
      "I create high quality digital products tailored to the creator's audience and positioning &mdash; courses, guides, templates, toolkits, communities.",
    icon: Layers,
    image:
      "https://static.prod-images.emergentagent.com/jobs/7224090e-fb57-4f5d-924e-aab3d13254dc/images/4c3ff4d10bf1d62f9eb5a745de294ffc5fefd96592d5f25b27c82eb19f64ea2b.png",
    span: "lg:col-span-7",
    height: "lg:h-[460px]",
  },
  {
    id: "launch-strategy",
    title: "Launch Strategy",
    description:
      "I help shape the offer into something clear, marketable, and ready to sell &mdash; positioning, pricing, and a launch plan that fits how you talk to your audience.",
    icon: Compass,
    image:
      "https://static.prod-images.emergentagent.com/jobs/7224090e-fb57-4f5d-924e-aab3d13254dc/images/f5d15fda9b58cba2f9fe47c68ae1d00be5df53ba3ea47ec03d791723ad39c46b.png",
    span: "lg:col-span-5",
    height: "lg:h-[460px]",
  },
  {
    id: "revenue-split",
    title: "Revenue Split Partnership",
    description:
      "I partner with creators on a split &mdash; you keep the majority, I handle the build. Aligned incentives, no upfront cost, real ownership of the outcome.",
    icon: Handshake,
    image:
      "https://images.unsplash.com/photo-1599576599260-2ce7202ceb06?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxjbGVhbiUyMG1pbmltYWxpc3QlMjBvZmZpY2UlMjBzZXR1cHxlbnwwfHx8fDE3Nzc1NTE0NDd8MA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-12",
    height: "lg:h-[420px]",
  },
];

export const Services = () => {
  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="border-b border-zinc-200/70 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <span className="section-label">What I do</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-zinc-900 tracking-tight leading-[1.05]">
              Three ways I work with creators.
            </h2>
          </div>
          <p className="text-zinc-500 max-w-md text-base md:text-lg">
            Pick what fits where you are. Most partnerships start with one and grow from there.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6">
          {SERVICES.map((s, i) => (
            <ServiceCard
              key={s.id}
              service={s}
              index={i + 1}
              onContact={scrollToContact}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ service, index, onContact }) => {
  const Icon = service.icon;
  return (
    <article
      data-testid={`service-card-${service.id}`}
      className={`group relative overflow-hidden rounded-3xl border border-zinc-200 bg-white shadow-sm hover:shadow-xl transition-all duration-500 ${service.span} ${service.height} flex flex-col`}
    >
      <div className="relative h-44 md:h-56 overflow-hidden">
        <img
          src={service.image}
          alt={service.title}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white/95 via-white/30 to-transparent" />
        <div className="absolute top-5 left-5 inline-flex items-center justify-center h-10 w-10 rounded-xl bg-white border border-zinc-200 shadow-sm">
          <Icon size={18} className="text-blue-700" />
        </div>
        <div className="absolute top-5 right-5 font-heading text-xs uppercase tracking-[0.2em] font-semibold text-zinc-900/70">
          0{index}
        </div>
      </div>

      <div className="flex-1 flex flex-col p-6 md:p-8 -mt-2">
        <h3 className="font-heading text-xl md:text-2xl font-semibold text-zinc-900 tracking-tight">
          {service.title}
        </h3>
        <p
          className="mt-3 text-zinc-600 text-base leading-relaxed"
          dangerouslySetInnerHTML={{ __html: service.description }}
        />
        <button
          data-testid={`service-cta-${service.id}`}
          onClick={onContact}
          className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-semibold text-zinc-900 hover:text-blue-700 transition-colors duration-200 self-start"
        >
          Talk about this
          <ArrowUpRight
            size={16}
            className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </button>
      </div>
    </article>
  );
};

export default Services;
